import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, RefreshCw, Trash2 } from "lucide-react";

interface Lead {
  id: string;
  created_at: string;
  name: string | null;
  phone: string | null;
  contract_type: string | null;
  source_page: string | null;
  notes: string | null;
  status: string;
}

interface Props {
  onDealCreated: () => void;
}

export const InboundLeadsTab = ({ onDealCreated }: Props) => {
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("new");
  const [converting, setConverting] = useState<string | null>(null);

  const fetchLeads = async () => {
    setLoading(true);
    let q = supabase
      .from("inbound_leads")
      .select("id, created_at, name, phone, contract_type, source_page, notes, status")
      .order("created_at", { ascending: false });
    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    const { data, error } = await q;
    if (error) {
      toast({ title: "Error loading leads", description: error.message, variant: "destructive" });
    } else {
      setLeads((data || []) as Lead[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const convertToDeal = async (lead: Lead) => {
    setConverting(lead.id);
    try {
      const title = `${lead.name || "Unnamed Lead"} - ${lead.contract_type || "Inbound"}`;
      const noteParts = [
        `Imported from website lead on ${new Date().toLocaleDateString()}`,
        lead.source_page ? `Source: ${lead.source_page}` : null,
        lead.contract_type ? `Contract type: ${lead.contract_type}` : null,
        lead.notes ? `Lead notes: ${lead.notes}` : null,
      ].filter(Boolean);

      const { error: insertErr } = await supabase.from("deals").insert({
        deal_title: title,
        seller_name: lead.name || "Unknown",
        seller_phone: lead.phone || null,
        buyer_name: "TBD",
        buyer_current_on_contract: true,
        has_balloon_payment: false,
        status: "new",
        internal_notes: noteParts.join("\n"),
      });
      if (insertErr) throw insertErr;

      const { error: updateErr } = await supabase
        .from("inbound_leads")
        .update({ status: "converted" })
        .eq("id", lead.id);
      if (updateErr) throw updateErr;

      toast({ title: "Lead converted", description: `Created deal: ${title}` });
      await fetchLeads();
      onDealCreated();
    } catch (e: any) {
      toast({ title: "Failed to convert lead", description: e.message, variant: "destructive" });
    } finally {
      setConverting(null);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("inbound_leads").update({ status }).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchLeads();
    }
  };

  const deleteLead = async (id: string) => {
    if (!confirm("Delete this lead?")) return;
    const { error } = await supabase.from("inbound_leads").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchLeads();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Leads submitted from the marketing website. Click "Convert to Deal" to create a draft deal prefilled with their info.
        </p>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchLeads}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No inbound leads.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Contract Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(lead.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-medium">{lead.name || "—"}</TableCell>
                    <TableCell>{lead.phone || "—"}</TableCell>
                    <TableCell>{lead.contract_type || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={lead.source_page || ""}>
                      {lead.source_page || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={lead.status === "converted" ? "secondary" : "default"}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {lead.status !== "converted" && (
                          <Button
                            size="sm"
                            onClick={() => convertToDeal(lead)}
                            disabled={converting === lead.id}
                          >
                            <ArrowRight className="h-3 w-3 mr-1" />
                            {converting === lead.id ? "..." : "Convert to Deal"}
                          </Button>
                        )}
                        {lead.status === "new" && (
                          <Button size="sm" variant="ghost" onClick={() => updateStatus(lead.id, "dismissed")}>
                            Dismiss
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => deleteLead(lead.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
